import React, { FC, useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../index";
import ButtonComponent from "../buttons/ButtonComponent";
import Selector from "../forms/selectors/Selector";
import { toast } from "react-toastify";
import SVG from "../svg/Svg";
import ConfirmToast from "../forms/notification/Confirm";
// `Н48`: отказ сервера показывается у поля, а не одним общим текстом.
import {
  errorMessageFrom,
  fieldErrorsFrom,
} from "../../utils/serverFieldErrors";

interface User {
  id: number;
  email: string;
  tel: string;
  password?: string;
  role?: string;
  roleId?: number;
  company_id?: number;
  companyId?: number;
  plant_id?: number | null;
  plantId?: number | null;
  station_id?: number | null;
  stationId?: number | null;
  stations?: { id: number; place: string | null }[];
}

const CreateUserForm: FC<{ close?: () => void; user?: User }> = ({
  close,
  user,
}) => {
  const { store } = useContext(Context);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [tel, setTel] = useState<string>("");
  const [roleId, setRoleId] = useState<number>(1);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [plantId, setPlantId] = useState<number | null>(null);
  const [stationId, setStationId] = useState<number | null>(null);
  const [detachStationIds, setDetachStationIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  // `Н48`: причина отказа — у самого поля, а не одним общим текстом внизу формы.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setTel(user.tel || "");
      // Найти ID роли по названию
      const roleOptions = [
        { id: 2, name: "оператор организации" },
        { id: 3, name: "оператор объекта" },
        { id: 4, name: "владелец станции" },
      ];
      const roleIdFromName =
        roleOptions.find((opt) => opt.name === user.role)?.id || 1;

      setRoleId(roleIdFromName);
      setCompanyId(user.company_id || user.companyId || null);
      setPlantId(user.plant_id || user.plantId || null);
      setStationId(user.station_id || user.stationId || null);
      setDetachStationIds([]);
    } else {
      setEmail("");
      setPassword("");
      setTel("");
      setRoleId(1);
      setCompanyId(null);
      setPlantId(null);
      setStationId(null);
      setDetachStationIds([]);
      setError(null);
    }
  }, [user]);

  useEffect(() => {
    if (roleId === 4 && plantId) {
      store.setPlant({ id: plantId } as any);
      store.getStations();
    }
  }, [plantId, roleId, store]);

  const filteredPlants = store.plants.filter(
    (plant) => plant.company_id === companyId,
  );

  const filteredStations = store.stations
    .filter(
      (station) =>
        station.plant_id === plantId &&
        !!station["Meters.meter_address"] &&
        station["Meters.meter_address"] !== "0" &&
        // DEV-56 (152-Q5 п. 1): занятость станции — has_owner, а не прямая
        // связь owner_id: иначе занятую станцию можно привязать второй раз.
        !station.has_owner,
    )
    .map((station) => ({
      id: station.id,
      name:
        station.serial ||
        station.ocpp_id ||
        station.address ||
        `Место ${station.place}`,
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFieldErrors({});
    setError(null);

    // `Н48`: адрес — это логин. При правке его не чистят формой: пустое поле
    // сервер отвергнет, но причина у человека должна быть про адрес, а не «ошибка
    // при сохранении». Заведение — другое дело, там адрес обязателен.
    if (isEditing && !email) {
      setFieldErrors({
        email: "адрес — это логин: меняется по обращению, а не правкой формы",
      });
      return;
    }

    if (!email || !roleId || !companyId) {
      setError("Заполните обязательные поля");
      return;
    }

    const OPERATOR_ROLE_ID = 3;
    const STATION_OWNER_ROLE_ID = 4;

    if (roleId === OPERATOR_ROLE_ID && !plantId) {
      setError('Для роли "оператор объекта" необходимо выбрать объект');
      return;
    }

    if (roleId === STATION_OWNER_ROLE_ID && !plantId) {
      setError('Для роли "владелец станции" необходимо выбрать объект');
      return;
    }

    try {
      if (isEditing && user) {
        await store.updateUserByAdmin({
          id: user.id,
          email,
          tel,
          password: password || undefined,
          stationId: roleId === 4 ? stationId || undefined : undefined,
          detachStationIds:
            detachStationIds.length > 0 ? detachStationIds : undefined,
        });
      } else {
        await store.createUser({
          email,
          tel,
          password,
          // `Н48`: имя поля роли — **одно**, `roleId` (сервер прежнее `roleID`
          // отвергает). Из-за `roleID` с сайта не заводился оператор.
          roleId: roleId,
          companyId: companyId || undefined,
          plantId: plantId || undefined,
          stationId: roleId === 4 ? stationId || undefined : undefined,
        });
      }
      close?.();
    } catch (err) {
      // `Н48`: причина отказа — у поля из ответа сервера, а не одним общим текстом.
      const byField = fieldErrorsFrom(err);

      if (Object.keys(byField).length > 0) {
        setFieldErrors(byField);
      } else {
        setError(errorMessageFrom(err, "Ошибка при сохранении пользователя"));
      }
    }
  };

  const handleDeleteUser = () => {
    // `АУД-Н156`: запись пользователя в консоль не пишем — это персональные данные.
    if (!user) return;

    toast(
      <ConfirmToast
        message="Вы уверены, что хотите удалить пользователя?"
        onConfirm={async () => {
          try {
            await store.DeleteUserByAdmin(user.id);
            toast.success("Пользователь успешно удалён");
            close?.();
          } catch (err) {
            toast.error("Ошибка при удалении пользователя");
          }
        }}
        type={"del"}
        title={"удалить"}
      />,
      {
        progressClassName: "toast-progress-delete",
        autoClose: false,
        closeOnClick: false,
      },
    );
  };

  /**
   * `Н48`: причина отказа сервера — **у поля**, к которому она относится: сервер
   * называет поле (`field`) и причину (`message`), значения не повторяет
   * (`АУД-Н159`). Прежде человек видел одно «Ошибка при сохранении пользователя».
   */
  const fieldError = (field: string) =>
    fieldErrors[field] ? (
      <div className="Error_message" data-field-error={field}>
        {fieldErrors[field]}
      </div>
    ) : null;

  return (
    <div className="loginForm_conteiner">
      <div className="createUserForm_close">
        <div onClick={close} className="createUserForm_close_btn">
          {SVG["close"]}
        </div>
      </div>
      <div className="loginform_title">
        {isEditing ? "Редактировать пользователя" : "Создать пользователя"}
      </div>
      <form onSubmit={handleSubmit}>
        <div className="loginForm_form">
          <label htmlFor="email">Логин или Email:</label>
          <input
            className="loginForm_input"
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {fieldError("email")}
        </div>

        {!isEditing && (
          <div className="loginForm_form">
            <label htmlFor="password">Пароль:</label>
            <input
              className="loginForm_input"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={!isEditing}
            />
            {fieldError("password")}
          </div>
        )}

        <div className="loginForm_form">
          <label htmlFor="tel">Телефон:</label>
          <input
            className="loginForm_input"
            id="tel"
            type="tel"
            value={tel}
            onChange={(e) => setTel(e.target.value)}
          />
          {fieldError("tel")}
        </div>

        <div className="loginForm_form">
          <label htmlFor="role">Роль:</label>
          <Selector
            options={[
              { id: 2, name: "оператор организации" },
              { id: 3, name: "оператор объекта" },
              { id: 4, name: "владелец станции" },
            ]}
            value={roleId}
            onChange={(value: number) => {
              setRoleId(value);
              if (value !== 3 && value !== 4) {
                setPlantId(null);
                setStationId(null);
              }
            }}
            placeholder="Выберите роль"
            disabled={isEditing}
          />
        </div>

        <div className="loginForm_form">
          <label htmlFor="company">Организация:</label>
          <Selector
            options={store.companies}
            value={companyId}
            onChange={(value: number) => setCompanyId(value)}
            placeholder="Выберите организацию"
            disabled={isEditing}
          />
        </div>

        {(roleId === 3 || roleId === 4) && (
          <div className="loginForm_form">
            <label htmlFor="plant">Объект:</label>
            <Selector
              options={filteredPlants}
              value={plantId}
              onChange={(value: number) => {
                setPlantId(value);
                setStationId(null);
              }}
              placeholder="Выберите объект"
              disabled={isEditing}
            />
          </div>
        )}

        {roleId === 4 && (
          <div className="loginForm_form">
            <label htmlFor="station">Станция:</label>
            <Selector
              options={filteredStations}
              value={stationId}
              onChange={(value: number) => setStationId(value)}
              placeholder="Выберите станцию"
            />
          </div>
        )}

        {isEditing &&
          roleId === 4 &&
          user?.stations &&
          user.stations.length > 0 && (
            <div className="loginForm_form">
              <label>Привязанные станции:</label>
              <ul style={{ margin: 0, paddingLeft: 20, listStyle: "none" }}>
                {user.stations
                  .filter((station) => !detachStationIds.includes(station.id))
                  .map((station) => (
                    <li
                      key={station.id}
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span>{station.place || `Станция ${station.id}`}</span>
                      <span
                        onClick={() =>
                          setDetachStationIds((prev) => [...prev, station.id])
                        }
                        style={{
                          cursor: "pointer",
                          color: "#d32f2f",
                          fontSize: 16,
                          lineHeight: 1,
                          userSelect: "none",
                        }}
                        title="Открепить станцию"
                      >
                        ×
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

        <div className="loginForm_btns">
          <ButtonComponent
            onClick={handleSubmit}
            title={isEditing ? "Сохранить" : "Создать пользователя"}
            size="100%"
          />
        </div>
      </form>

      {isEditing && (
        <div className="loginForm_btns">
          <ButtonComponent
            onClick={handleDeleteUser}
            title={"удалить"}
            size="100%"
            type="del"
          />
        </div>
      )}

      {error && (
        <div className="Error_message">
          <div className="Error_message_title">Ошибка:</div>
          {error}
        </div>
      )}
    </div>
  );
};

export default observer(CreateUserForm);
